import { Link } from 'react-router-dom';
import { Phone } from 'lucide-react';
import { Card, CardContent } from '@paulownia/shared/components/ui/card';
import type { TenantWithMarketInfo } from '@paulownia/shared';
import { ActivityBadge } from './ActivityBadge';

interface MerchantCardProps {
  tenant: TenantWithMarketInfo;
  marketId?: string;
}

function ProfilePhotoPlaceholder({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <div className="h-full w-full flex items-center justify-center bg-orange-100">
      <span className="text-2xl font-semibold text-orange-500">{initials}</span>
    </div>
  );
}

/**
 * Card component displaying a merchant in the market grid
 */
export function MerchantCard({ tenant, marketId }: MerchantCardProps) {
  const isAbsent = tenant.isAbsent;
  const linkTo = marketId ? `/${tenant.slug}?market=${marketId}` : `/${tenant.slug}`;

  return (
    <Link to={linkTo}>
      <Card className={`transition-shadow cursor-pointer h-full overflow-hidden ${
        isAbsent
          ? 'opacity-60 bg-gray-50'
          : 'hover:shadow-md'
      }`}>
        <div className="h-32 w-full overflow-hidden bg-orange-50">
          {tenant.profilePhoto ? (
            <img
              src={tenant.profilePhoto}
              alt={tenant.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <ProfilePhotoPlaceholder name={tenant.name} />
          )}
        </div>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <h3 className={`font-semibold text-lg ${isAbsent ? 'text-gray-500' : ''}`}>
              {tenant.name}
            </h3>
            {isAbsent && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600 flex-shrink-0">
                Absent
              </span>
            )}
          </div>
          {tenant.description && (
            <p className={`text-sm mt-1 ${
              isAbsent ? 'text-gray-400' : 'text-muted-foreground'
            }`}>
              {tenant.description}
            </p>
          )}
          {tenant.phoneNumber && (
            <p className={`flex items-center gap-1.5 text-sm mt-2 ${
              isAbsent ? 'text-gray-400' : 'text-muted-foreground'
            }`}>
              <Phone className="h-3.5 w-3.5 flex-shrink-0" />
              {tenant.phoneNumber}
            </p>
          )}
          {tenant.activityTypes.length > 0 && (
            <div className={`flex flex-wrap gap-1 mt-2 ${isAbsent ? 'opacity-50' : ''}`}>
              {tenant.activityTypes.map((type) => (
                <ActivityBadge key={type} type={type} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}
